<template>
  <div>
    <div v-if="!isPartner" :overrideClass="isNotGrid ? 'not-grid' : ''">
      <FormLabel v-if="!isManualAddress" labelText="Residential address" required forId="residential-address-input" />
      <div :id="helperText ? 'address-help-text-wrapper' : ''">
        <AddressForm
          id="residential-address-input"
          @updateAddressDetail="emitAddressDetails"
          @updateManualAddressEntry="changeEnterAddressManually"
          :preFilledDetails="preFilledResidentialAddress"
          :residentialState="preFilledResidentialState"
          :isPOBox="false"
          :prefillSearchKey="false"
          :stateLocked="stateLocked"
          :disabled="disableField"
          :isNotGrid="isNotGrid"
        />
        <div v-if="helperText" class="helper-text fr-view">
          <p v-html="helperText" />
        </div>
      </div>
      <Alert v-if="showResidentialAlert" alertType="info" class="inline-message">
        <div>
          <p>Updates to residential address will reflect across all policies where this person is the policy holder, and may result in changes to premium.</p>
        </div>
      </Alert>
    </div>
    <FormField v-else :overrideClass="isNotGrid ? 'not-grid' : ''">
      <p>While one residential address is recorded on the membership, a different postal address may be recorded.</p>
    </FormField>
    <FormField v-if="!isExistingMember" :overrideClass="isNotGrid ? 'not-grid' : ''">
      <input
        type="checkbox"
        :id="!parentId ? 'differentPostalAddress' : `differentPostalAddress-${parentId}`"
        v-model.lazy="differentPostalAddress"
        @change="emitAddrValidation()"
        @keyup.enter.space="differentPostalAddress = !differentPostalAddress"
      />
      <label :for="!parentId ? 'differentPostalAddress' : `differentPostalAddress-${parentId}`">{{ addressFormLabelText.differentPostalAddressLabel }}</label>
    </FormField>
    <div v-show="isExistingMember || differentPostalAddress">
      <FormField
        :labelText="addressFormLabelText.isPOBoxLabel"
        class="form-section"
        :overrideClass="isNotGrid ? 'not-grid' : ''"
        :helperText="showPostalAddressHelperText ? postalAddressHelpText : ''"
      >
        <ul class="radio-group large">
          <li>
            <input
              :id="!parentId ? 'POBoxYes' : `POBoxYes -${parentId}`"
              :value="true"
              :name="!parentId ? 'POBox' : `POBox -${parentId}`"
              type="radio"
              v-model.lazy="isPOBox"
              @change="emitAddrValidation()"
            />
            <label :for="!parentId ? 'POBoxYes' : `POBoxYes -${parentId}`">Yes</label>
          </li>
          <li>
            <input
              :id="!parentId ? 'POBoxNo' : `POBoxNo -${parentId}`"
              :value="false"
              :name="!parentId ? 'POBox' : `POBox -${parentId}`"
              type="radio"
              v-model.lazy="isPOBox"
              @change="emitAddrValidation()"
            />
            <label :for="!parentId ? 'POBoxNo' : `POBoxNo -${parentId}`">No</label>
          </li>
        </ul>
      </FormField>

      <FormField v-if="isPOBox !== null" :overrideClass="isNotGrid ? 'not-grid' : ''">
        <FormLabel v-if="!isManualAddress" labelText="Postal address" required :forId="!parentId ? 'postal-address-input' : `postal-address-input-${parentId}`" />
        <AddressForm
          :id="!parentId ? 'postal-address-input' : `postal-address-input-${parentId}`"
          @updateAddressDetail="emitAddressDetailsPostal"
          @updateManualAddressEntry="changeEnterAddressManually"
          :preFilledDetails="preFilledPostalAddress"
          :isPOBox="this.isPOBox"
        />
      </FormField>
      <Alert v-if="showPostalAlert" alertType="info" class="inline-message">
        <div>
          <p>Updates to postal address will reflect across all policies you hold with us.</p>
        </div>
      </Alert>
    </div>
  </div>
</template>
<script>
import isEqual from 'lodash.isequal';
import 'regenerator-runtime';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { FormField, FormLabel, Alert } from '@/components';
import AddressForm from './AddressForm.vue';

export default {
  setup() { return { v$: useVuelidate() }; },
  components: {
    AddressForm,
    FormField,
    FormLabel,
    Alert,
  },

  data() {
    return {
      differentPostalAddress: this.preFillledDifferentPostalAddress,
      isPOBox: this.preFilledPOBox,
      postalAddressInvalid: this.preFillledDifferentPostalAddress,
      residentialAddressInvalid: !this.isPartner,
      isManualAddress: false,
      postalAddress: null,
      residentialAddress: null,
    };
  },

  props: {
    isPartner: {
      type: Boolean,
      default: false,
    },
    isExistingMember: {
      type: Boolean,
      default: false,
    },
    preFilledResidentialAddress: {
      type: Object,
    },
    preFilledResidentialState: {
      type: String,
    },
    // If true, state can not be edited and `preFilledResidentialState` prop must be given.
    stateLocked: {
      type: Boolean,
      default: false,
    },
    preFilledPostalAddress: {
      type: Object,
    },
    preFilledPOBox: {
      type: Boolean,
      default: null,
    },
    preFillledDifferentPostalAddress: {
      type: Boolean,
      default: false,
    },
    isNotGrid: {
      type: Boolean,
      default: false,
    },
    disableField: {
      type: Boolean,
      default: false,
    },
    helperText: {
      type: String,
      default: '',
    },
    parentId: {
      type: String,
      default: '',
    },
    isChangeCover: {
      type: Boolean,
      default: false,
    },
    showResidentialAlert: {
      type: Boolean,
      default: false,
    },
    showPostalAlert: {
      type: Boolean,
      default: false,
    },
    showPostalAddressHelperText: {
      type: Boolean,
      default: false,
    },
    postalAddressHelpText: {
      type: String,
      default: '',
    },
  },

  validations: {
    postalAddressInvalid: {
      isValid(bool) {
        return bool === false || !this.differentPostalAddress;
      },
    },
    residentialAddressInvalid: {
      isValid(bool) {
        return bool === false;
      },
    },
    isPOBox: {
      required: requiredIf(function () {
        return this.differentPostalAddress;
      }),
    },
  },

  computed: {
    addressFormLabelText() {
      const policyHolderLabels = {
        differentPostalAddressLabel: 'I have a different postal address',
        isPOBoxLabel: 'Is your postal address a Post Office box (PO Box)?',
      };

      const partnerLabels = {
        differentPostalAddressLabel: 'Your partner has a different postal address',
        isPOBoxLabel: "Is your partner's postal address a Post Office box (PO Box)?",
      };

      return this.isPartner ? partnerLabels : policyHolderLabels;
    },
  },

  watch: {
    postalAddress() {
      if (this.isExistingMember) {
        this.differentPostalAddress = !isEqual(this.postalAddress, this.residentialAddress);
      }
    },

    differentPostalAddress(newVal) {
      this.isPOBox = this.preFilledPOBox;

      if (newVal === false) {
        this.emitAddressDetailsPostal({
          invalid: newVal,
          differentPostalAddress: newVal,
        });
      } else {
        this.resetPostalAddress();
      }

      this.v$.postalAddressInvalid.$reset();
    },
    isPOBox() {
      this.resetPostalAddress();
    },
  },

  methods: {
    resetPostalAddress() {
      if (this.differentPostalAddress) {
        this.emitAddressDetailsPostal({
          address: {
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            suburb: '',
            postcode: '',
            state: '',
            dpId: '',
          },
          invalid: true,
        });
        this.postalAddressInvalid = true;
      }
    },
    emitAddrValidation() {
      const data = {
        differentPostalAddress: this.differentPostalAddress,
        isPOBox: this.isPOBox,
      };
      this.$emit('updateAddressValidation', data);
    },
    emitAddressDetails(data) {
      this.residentialAddressInvalid = data.invalid;
      this.residentialAddress = data.address;
      this.emitAddress(data, 'residential');
    },
    emitAddressDetailsPostal(data) {
      this.postalAddressInvalid = data.invalid;
      this.postalAddress = data.address;
      this.emitAddress(data, 'postal');
    },
    emitAddress(data, type) {
      data.differentPostalAddress = this.differentPostalAddress;
      this.$emit('updateAddressDetails', data, type, this.v$.$invalid);
    },
    changeEnterAddressManually(data) {
      this.isManualAddress = data;
    },
  },
};
</script>

<style lang="scss" scoped>
.helper-text {
  & > p,
  & > div {
    background-color: $WHITE;
    padding: 12px 24px;
    border-left: 3px solid $LIGHT_GREEN;
    border-radius: 2px;
    font-weight: $NORMAL;
    font-size: $CAPTION_FONT_SIZE;
    color: $GREY_800;
    box-shadow: 0 2px 6px rgba($BLACK, 0.06);
  }
}
#address-help-text-wrapper {
  display: grid;
  @include grid-layout(null, spacer(1));
  @include grid-template(2fr 1fr);
  row-gap: 0;
  margin-bottom: 2rem;
}
</style>
